import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Eye, EyeOff, Mail, Lock, KeyRound, AlertCircle, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  
  const { login, loginWithGoogle, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Clear error when inputs change
  useEffect(() => {
    setLoginError('');
  }, [email, password]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!email.trim()) {
      setLoginError('Please enter your email address');
      return;
    }
    
    if (!password) {
      setLoginError('Please enter your password');
      return;
    }

    setLoading(true);
    setLoginError('');
    
    try {
      await login(email.trim(), password);
      toast.success('Login successful!');
      navigate('/dashboard', { replace: true });
    } catch (error) {
      console.error('Login error:', error);
      
      // User-friendly error messages
      let errorMessage = error.message || 'Login failed';
      
      if (errorMessage.includes('Invalid credentials')) {
        errorMessage = 'Invalid email or password. Please try again.';
      } else if (errorMessage.includes('Network error') || errorMessage.includes('fetch')) {
        errorMessage = 'Unable to connect to server. Please check your internet connection.';
      } else if (errorMessage.includes('CORS')) {
        errorMessage = 'Server configuration error. Please contact support.';
      }
      
      setLoginError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    
    if (!forgotEmail.trim()) {
      toast.error('Please enter your email address');
      return;
    }
    
    setForgotLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail.trim() })
      });
      
      // Read as text first for stability
      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        data = { message: text };
      }
      
      if (response.ok) {
        toast.success(data.message || 'Password reset request submitted');
        setShowForgotPassword(false);
        setForgotEmail('');
      } else {
        toast.error(data.detail || data.message || 'Failed to submit request');
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    try {
      loginWithGoogle();
    } catch (error) {
      console.error('Google login error:', error);
      toast.error('Failed to initiate Google login');
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-gradient-to-b from-[#0066CC]/5 via-white to-[#FF6600]/5">
      {/* Main Container */}
      <div className="w-full max-w-md">
        {/* Logo & Branding */}
        <div className="text-center mb-10">
          {/* Logo */}
          <img 
            src="https://customer-assets.emergentagent.com/job_457f63c4-6a2a-4995-8167-0b04779a6e2e/artifacts/c21k9zo8_bylyngo%20interpreting.png" 
            alt="Bylyngo Interpreting"
            className="h-28 w-auto mx-auto mb-6"
          />
          
          {/* Accent line */}
          <div className="flex items-center justify-center gap-2 mb-2">
            <div className="h-1 w-12 bg-[#0066CC] rounded-full"></div>
            <div className="h-1 w-8 bg-[#FF6600] rounded-full"></div>
            <div className="h-1 w-12 bg-[#0066CC] rounded-full"></div>
          </div>
        </div>

        {/* Login Card */}
        <Card className="border-0 shadow-2xl shadow-[#0066CC]/10">
          <CardContent className="p-8">
            <h3 className="text-xl font-semibold text-center mb-6 text-gray-800">
              Sign in to your account
            </h3>

            {/* Error Display */}
            {loginError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2" data-testid="login-error">
                <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{loginError}</p>
              </div>
            )}

            {/* Google Login */}
            <Button 
              variant="outline" 
              className="w-full h-12 gap-3 border-2 hover:bg-gray-50 transition-all"
              onClick={handleGoogleLogin}
              disabled={loading}
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>

            <div className="relative my-6">
              <Separator />
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-3 text-sm text-gray-400">
                or
              </span>
            </div>

            {/* Email/Password form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-700">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-11 h-12 border-2 focus:border-[#0066CC] transition-colors"
                    autoComplete="email"
                    autoFocus
                    disabled={loading}
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-700">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-11 pr-11 h-12 border-2 focus:border-[#0066CC] transition-colors"
                    autoComplete="current-password"
                    disabled={loading}
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                <div className="text-right">
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm text-[#0066CC] hover:text-[#0055AA] transition-colors"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </button>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 bg-[#0066CC] hover:bg-[#0055AA] text-lg font-semibold shadow-lg shadow-[#0066CC]/30 transition-all hover:shadow-xl hover:shadow-[#0066CC]/40"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>

            <p className="mt-6 text-center text-sm text-gray-500">
              Don&apos;t have an account?{' '}
              <Link to="/register" className="text-[#0066CC] hover:text-[#0055AA] font-semibold transition-colors" data-testid="register-link">
                Create account
              </Link>
            </p>
          </CardContent>
        </Card>

        {/* Forgot Password Dialog */}
        <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <KeyRound className="h-5 w-5 text-[#0066CC]" />
                Forgot Password
              </DialogTitle>
              <DialogDescription>
                Enter your email address and we&apos;ll submit a password reset request. A Super Admin will review and approve your request.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleForgotPassword} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="forgot-email">Email Address</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    id="forgot-email"
                    type="email"
                    placeholder="you@example.com"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    className="pl-11 h-12"
                    autoComplete="email"
                    disabled={forgotLoading}
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>
              <Button 
                type="submit" 
                className="w-full bg-[#0066CC] hover:bg-[#0055AA]"
                disabled={forgotLoading}
                data-testid="forgot-submit-btn"
              >
                {forgotLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Request'
                )}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Creator Credit */}
        <div className="mt-10 text-center">
          <p className="text-sm text-gray-400">Created by</p>
          <p className="text-base font-semibold text-[#FF6600]">Dawn Pérez Maldonado</p>
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-xs text-gray-400">
          © {new Date().getFullYear()} Bylyngo Language Services. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default Login;
